import React, {useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";

// @mui/material components
import {FormControlLabel, Switch} from "@mui/material";

// @mui/icons-material
import {NotificationsActive} from "@mui/icons-material";

// core components
import AllTranslationsRequiredMessage from "../../configuration/components/AllTranslationsRequiredMessage";
import FormFields from './formFields';
import GridItem from "components/grid/gridItem";
import ActionsContainer from "components/form/ActionsContainer";
import FormContainer from "views/configuration/components/FormContainer";
import CancelButton from "views/configuration/components/CancelButton";
import SubmitButton from "views/configuration/components/SubmitButton";
import Snackbar from "components/snackbar/snackbar";
import store from '../../../store';

// api
import update from "api/brand/update";
import {handleFileUpload, removeFile} from "api/upload";

// styles
import FormTranslator from "../../../components/translator/formTranslator";
import {localeData} from "../../../domain/helpers/translations";
import CtaLinksSection from "../../../components/ctaLinks/CtaLinksSection";
import {areCtaLinksSubmittable, ctaLinksSubmitValue, withCtaLinkClientIds} from "../../../components/ctaLinks/helpers";

function translationValues(translations, field) {
    const values = {};
    for (let code in translations) {
        values[code] = translations[code][field];
    }

    return values;
}

// Brand editing is content-only: the name (read-only) is the functional key pushed to the Sylius
// taxon code, so it is never editable here. Translatable content is description/longDescription/picture;
// brand-level fields are the `enabled` visibility toggle and the CTA links.
function EditForm(props) {
    const { brand, closeDialog, onUpdated } = props;
    const intl = useIntl();

    const [enabled, setEnabled] = useState(!!brand.enabled);
    const [descriptions, setDescriptions] = useState(() => translationValues(brand.translations, 'description'));
    const [longDescriptions, setLongDescriptions] = useState(() => translationValues(brand.translations, 'longDescription'));
    const [pictures, setPictures] = useState(() => translationValues(brand.translations, 'picture'));
    const [ctaLinks, setCtaLinks] = useState(withCtaLinkClientIds(brand.ctaLinks ?? []));
    const [updateLoading, setUpdateLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [isErrorDisplayed, setErrorDisplayed] = useState(false);

    const organizationLocales = localeData(store.getState().currentOrganization?.retrieved?.locales ?? []);

    const isFormValid = areCtaLinksSubmittable(ctaLinks, organizationLocales.map((locale) => locale.code));

    const uploadPicture = async (files, code) => {
        setUploadLoading(true);

        try {
            const mediaObject = await handleFileUpload(files, "brand_translation_picture");
            setPictures((previous) => ({...previous, [code]: mediaObject}));
        } catch (error) {
            setErrorDisplayed(true);
        } finally {
            setUploadLoading(false);
        }
    };

    async function submit(e) {
        e.preventDefault();

        setUpdateLoading(true);

        try {
            // A picture removed in the form also deletes the media object itself.
            for (let code in brand.translations) {
                const originalPicture = brand.translations[code].picture;

                if (originalPicture && !pictures[code]) {
                    await removeFile(originalPicture['@id'] ?? originalPicture);
                }
            }

            await update(brand, {
                enabled,
                descriptions,
                longDescriptions,
                pictures,
                // Empty rows are stripped; null stays null when the feature was never used on this brand,
                // so the Marketplace keeps omitting the field from the Sylius push.
                ctaLinks: ctaLinksSubmitValue(ctaLinks, brand.ctaLinks),
            });

            // The parent closes the dialog and refreshes the list; keep the button spinner
            // running during the close animation.
            onUpdated();
        } catch (error) {
            setErrorDisplayed(true);
            setUpdateLoading(false);
        }
    }

    const renderTranslationForm = (index, code) => {
        return (
            <div>
                <GridItem xs={12} sm={12}>
                    <FormFields
                        name={`translations.${code}.description`}
                        label={intl.formatMessage({id: "brand.form.label.description"})}
                        type={"wysiwyg"}
                        value={descriptions[code]}
                        onChange={(value) => setDescriptions((previous) => ({...previous, [code]: value}))}
                    />
                </GridItem>
                <GridItem xs={12} sm={12}>
                    <FormFields
                        name={`translations.${code}.longDescription`}
                        label={intl.formatMessage({id: "brand.form.label.longDescription"})}
                        type={"wysiwyg"}
                        value={longDescriptions[code]}
                        onChange={(value) => setLongDescriptions((previous) => ({...previous, [code]: value}))}
                    />
                </GridItem>
                <GridItem xs={12} sm={12}>
                    <FormFields
                        name={`translations.${code}.picture`}
                        label={intl.formatMessage({id: "brand.form.label.image"})}
                        type={"file"}
                        picture={pictures[code]}
                        uploadLoading={uploadLoading}
                        onDrop={(files) => uploadPicture(files, code)}
                        onDeletePicture={() => setPictures((previous) => ({...previous, [code]: null}))}
                    />
                </GridItem>
            </div>
        );
    };

    return (
        <form onSubmit={(e) => submit(e)}>
            <FormContainer>
                <AllTranslationsRequiredMessage />
                <GridItem xs={12} sm={12}>
                    <FormFields
                        name={"name"}
                        label={intl.formatMessage({id: "brand.form.label.name"})}
                        type={"text"}
                        value={brand.name}
                        disabled
                    />
                </GridItem>
                <GridItem xs={12} sm={12}>
                    <FormControlLabel
                        control={<Switch
                            checked={enabled}
                            onChange={(event) => setEnabled(event.target.checked)}
                            color="primary"
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />}
                        label={intl.formatMessage({id: "brand.form.label.enabled"})}
                    />
                </GridItem>
                <GridItem xs={12} sm={12}>
                    <FormTranslator formGenerator={renderTranslationForm} />
                </GridItem>
                <GridItem xs={12} sm={12}>
                    <CtaLinksSection value={ctaLinks} onChange={setCtaLinks} locales={organizationLocales} />
                </GridItem>
            </FormContainer>
            <ActionsContainer>
                <CancelButton onClick={closeDialog} />
                <SubmitButton
                    isEditing={true}
                    disabled={!isFormValid || updateLoading || uploadLoading}
                    loading={updateLoading}
                />
            </ActionsContainer>
            <Snackbar
                open={isErrorDisplayed}
                close
                closeNotification={() => setErrorDisplayed(false)}
                place={"bl"}
                color={"danger"}
                icon={() => <NotificationsActive />}
                message={<FormattedMessage id={"list.error"} />}
            />
        </form>
    );
}

export default EditForm;
